import { create } from 'zustand';
import { supabase, Simulation, User, SimulationReport } from './supabase';

interface ManagerState {
    personnel: User[];
    simulations: Simulation[];
    activePersonnel: User | null; // Seçili personel
    activePersonnelReports: SimulationReport[]; // Seçili personelin raporları
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchPersonnel: () => Promise<void>;
    fetchSimulations: () => Promise<void>;
    fetchPersonnelReports: (userId: string) => Promise<void>;

    // Summary
    userSummary: any | null;
    isGeneratingSummary: boolean;

    fetchUserSummary: (userId: string) => Promise<void>;
    generateUserSummary: (userId: string) => Promise<void>;

    setActivePersonnel: (user: User | null) => void;

    // Simulation Actions (Specific to Manager's institution)
    addSimulation: (title: string, config: any) => Promise<{ success: boolean; error?: string }>;
    updateSimulation: (id: string, updates: Partial<Simulation>) => Promise<{ success: boolean; error?: string }>;
    deleteSimulation: (id: string) => Promise<{ success: boolean; error?: string }>;
}

export const useManagerStore = create<ManagerState>((set, get) => ({
    personnel: [],
    simulations: [],
    activePersonnel: null,
    activePersonnelReports: [],
    userSummary: null,
    isLoading: false,
    isGeneratingSummary: false,
    error: null,

    setActivePersonnel: (user) => {
        set({ activePersonnel: user, activePersonnelReports: [], userSummary: null });
        if (user) {
            get().fetchPersonnelReports(user.id);
            get().fetchUserSummary(user.id);
        }
    },

    fetchUserSummary: async (userId) => {
        // 1. Check DB for today's summary
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const today = new Date().toISOString().split('T')[0];
            const { data } = await supabase
                .from('user_performance_summaries')
                .select('*')
                .eq('user_id', userId)
                .eq('summary_date', today)
                .single();

            if (data) {
                set({ userSummary: data.summary_data });
                return;
            }

            // If not found, check if we need to generate it (only if there are reports)
            // We'll trigger generation if no summary exists for today and user has reports
            // But to avoid infinite loops or unnecessary API calls, let's leave explicit generation trigger to the UI or a separate logic
            // providing "null" means UI will show "Generating..." or "Generate" button.
            // For this requirement: "update every 24h", we effectively check if the last summary is old. 
            // If data is null, we can try to find ANY recent summary.

            // (Unused query removed to fix lint error)

            // If last summary exists and is from today, use it (covered above). 
            // If it's from yesterday or older, or doesn't exist, we set null and UI triggers generation.
            // However, the prompt says "every morning at 6 AM". 
            // Let's rely on the UI component to call `generateUserSummary` if this returns null.
            set({ userSummary: null });

        } catch (err) {
            console.error("Error fetching summary:", err);
            set({ userSummary: null });
        }
    },

    generateUserSummary: async (userId) => {
        set({ isGeneratingSummary: true });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            // 1. Fetch last 20 reports
            const { data: reports } = await supabase
                .from('simulation_reports')
                .select('simulation_title, score, report_data, created_at')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(20);

            if (!reports || reports.length === 0) {
                set({ isGeneratingSummary: false, userSummary: { overview: "Henüz analiz edilecek veri yok." } });
                return;
            }

            // 2. Prepare context for Gemini
            const summaries = reports.map(r => {
                const d = typeof r.report_data === 'string' ? JSON.parse(r.report_data) : r.report_data;
                return `
- Tarih: ${new Date(r.created_at).toLocaleDateString()}
- Simülasyon: ${r.simulation_title}
- Puan: ${r.score}
- Genel Yorum: ${d.shortAnalysis || ''}
- Gelişim Alanları: ${d.improvements?.join(', ') || ''}
`.trim();
            }).join('\n');

            const prompt = `
Aşağıdaki verilere dayanarak, bu satış personeli için günlük performans özetini oluştur.
Son 20 Simülasyon Verisi:
${summaries}

ÇIKTI FORMATI (JSON):
{
  "overview": "Genel performans özeti (kısa paragraf)",
  "positives": ["Madde 1", "Madde 2", "Madde 3"],
  "negatives": ["Madde 1", "Madde 2", "Madde 3"],
  "improvements": ["Öneri 1", "Öneri 2", "Öneri 3"]
}
Sadece JSON döndür.
`.trim();

            const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        contents: [{ parts: [{ text: prompt }] }],
                        generationConfig: { responseMimeType: "application/json" }
                    })
                }
            );

            const result = await response.json();
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text;

            if (!text) throw new Error("AI yanıt veremedi");

            const summaryData = JSON.parse(text);

            // 3. Save to DB
            const today = new Date().toISOString().split('T')[0];

            // Upsert mechanism to avoid race conditions roughly
            const { error } = await supabase
                .from('user_performance_summaries')
                .upsert({
                    user_id: userId,
                    summary_date: today,
                    summary_data: summaryData
                }, { onConflict: 'user_id, summary_date' });

            if (error) console.error("DB Save Error:", error);

            set({ userSummary: summaryData });

        } catch (error) {
            console.error("Summary Generation Error:", error);
        } finally {
            set({ isGeneratingSummary: false });
        }
    },

    fetchPersonnel: async () => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            // Mevcut kullanıcının (müdürün) kurumunu al
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { data: managerProfile } = await supabase
                .from('profiles')
                .select('institution_id')
                .eq('id', user.id)
                .single();

            if (!managerProfile?.institution_id) throw new Error('Institution not found');

            // Müdürün kurumundaki personelleri çek (Müdürler dahil olabilir veya hariç tutulabilir, şu an hepsi)
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('institution_id', managerProfile.institution_id)
                .order('full_name');

            if (error) throw error;

            // Tip dönüşümü
            const typedUsers: User[] = data.map((p: any) => ({
                id: p.id,
                email: p.email || '', // Email profile'da olmayabilir auth'dan gelir ama join karmaşık, şimdilik boş
                full_name: p.full_name,
                role: p.role,
                institution_id: p.institution_id,
                created_at: p.created_at,
                avatar_url: p.avatar_url
            }));

            set({ personnel: typedUsers });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    fetchSimulations: async () => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { data: managerProfile } = await supabase
                .from('profiles')
                .select('institution_id')
                .eq('id', user.id)
                .single();

            if (!managerProfile?.institution_id) throw new Error('Institution not found');

            const { data, error } = await supabase
                .from('simulations')
                .select('*')
                .eq('institution_id', managerProfile.institution_id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            set({ simulations: data || [] });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    fetchPersonnelReports: async (userId) => {
        set({ isLoading: true, error: null }); // Don't clear activePersonnel here
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { data, error } = await supabase
                .from('simulation_reports')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            set({ activePersonnelReports: data || [] });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    addSimulation: async (title, config) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { data: managerProfile } = await supabase
                .from('profiles')
                .select('institution_id')
                .eq('id', user.id)
                .single();

            if (!managerProfile?.institution_id) throw new Error('Institution not found');

            const { error } = await supabase
                .from('simulations')
                .insert([{
                    institution_id: managerProfile.institution_id,
                    title,
                    config,
                    is_active: true
                }]);

            if (error) throw error;

            await get().fetchSimulations();
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    updateSimulation: async (id, updates) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase
                .from('simulations')
                .update(updates)
                .eq('id', id);

            if (error) throw error;
            await get().fetchSimulations();
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    deleteSimulation: async (id) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase.from('simulations').delete().eq('id', id);
            if (error) throw error;
            await get().fetchSimulations();
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    }
}));
